/**
 * Rich Text Editor Component
 * Provides formatting capabilities for case notes
 */

import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link as LinkIcon,
  Type
} from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (content: string, html: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Enter your note here...',
  className = '',
  minHeight = '200px'
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Initialize content
  useEffect(() => {
    if (editorRef.current && !editorRef.current.innerHTML && value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const handleInput = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      const text = editorRef.current.innerText;
      onChange(text, html);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const formatButtons = [
    { icon: Bold, command: 'bold', title: 'Bold (Ctrl+B)' },
    { icon: Italic, command: 'italic', title: 'Italic (Ctrl+I)' },
    { icon: Underline, command: 'underline', title: 'Underline (Ctrl+U)' },
  ];

  const alignButtons = [
    { icon: AlignLeft, command: 'justifyLeft', title: 'Align Left' },
    { icon: AlignCenter, command: 'justifyCenter', title: 'Align Center' },
    { icon: AlignRight, command: 'justifyRight', title: 'Align Right' },
  ];

  const listButtons = [
    { icon: List, command: 'insertUnorderedList', title: 'Bullet List' },
    { icon: ListOrdered, command: 'insertOrderedList', title: 'Numbered List' },
  ];

  return (
    <div className={`border-2 rounded-xl overflow-hidden transition-all ${isFocused ? 'border-blue-500 ring-4 ring-blue-100' : 'border-blue-300'} ${className}`}>
      {/* Toolbar */}
      <div className="bg-gradient-to-r from-gray-50 to-blue-50 border-b border-blue-200 p-2 flex flex-wrap gap-1">
        {/* Text Formatting */}
        <div className="flex gap-1 border-r border-gray-300 pr-2">
          {formatButtons.map(({ icon: Icon, command, title }) => (
            <Button
              key={command}
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => execCommand(command)}
              title={title}
              className="h-8 w-8 p-0 hover:bg-blue-100"
            >
              <Icon className="w-4 h-4" />
            </Button>
          ))}
        </div>

        {/* Lists */}
        <div className="flex gap-1 border-r border-gray-300 pr-2">
          {listButtons.map(({ icon: Icon, command, title }) => (
            <Button
              key={command}
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => execCommand(command)}
              title={title}
              className="h-8 w-8 p-0 hover:bg-blue-100"
            >
              <Icon className="w-4 h-4" />
            </Button>
          ))}
        </div>

        {/* Alignment */}
        <div className="flex gap-1 border-r border-gray-300 pr-2">
          {alignButtons.map(({ icon: Icon, command, title }) => (
            <Button
              key={command}
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => execCommand(command)}
              title={title}
              className="h-8 w-8 p-0 hover:bg-blue-100"
            >
              <Icon className="w-4 h-4" />
            </Button>
          ))}
        </div>

        {/* Font Size */}
        <div className="flex gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => execCommand('fontSize', '5')}
            title="Large Text"
            className="h-8 px-2 hover:bg-blue-100 text-xs"
          >
            <Type className="w-4 h-4 mr-1" />
            Large
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => execCommand('fontSize', '3')}
            title="Normal Text"
            className="h-8 px-2 hover:bg-blue-100 text-xs"
          >
            <Type className="w-3 h-3 mr-1" />
            Normal
          </Button>
        </div>
      </div>

      {/* Editor Content */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onPaste={handlePaste}
        className="p-4 focus:outline-none prose prose-sm max-w-none"
        style={{ minHeight }}
        data-placeholder={placeholder}
      />

      <style jsx>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
}
